import { createContext, useContext, useRef, type ReactNode } from 'react';
import { CmsComponentSwitch } from 'src/cms/CmsComponentSwitch';
import ModalPane from 'src/components/Surfaces/Modal/ModalPane';
import {
  PaywallProvider,
  usePaywallProvider,
} from 'src/components/Surfaces/Paywall/PaywallProvider';
import { FormTypes } from 'src/lib/constants';

import { requestVideoPlayback } from './requestVideoPlayback';

type GateEntry = {
  fields?: Record<string, unknown>;
  sys?: { id?: string };
};

type VideoGateContextValue = {
  requestPlayback: (start: () => void) => void;
};

const VideoGateContext = createContext<VideoGateContextValue | null>(null);

export const useVideoGate = () => {
  return useContext(VideoGateContext);
};

const secretKeyOf = (gate?: GateEntry | null) => {
  const secretKey = gate?.fields?.secretKey;
  return typeof secretKey === 'string' && secretKey ? secretKey : '';
};

const VideoGateInner = ({
  gate,
  children,
}: {
  gate: GateEntry;
  children: ReactNode;
}) => {
  const pendingPlay = useRef<(() => void) | null>(null);
  const {
    handleUnlockContent,
    isContentUnlocked,
    isModalOpen,
    setIsModalOpen,
  } = usePaywallProvider();
  const form = gate.fields?.components;

  const requestPlayback = (start: () => void) => {
    requestVideoPlayback({
      hasGate: true,
      isUnlocked: isContentUnlocked,
      start,
      openGate: () => {
        pendingPlay.current = start;
        setIsModalOpen(true);
      },
    });
  };

  const closeModal = (isOpen: boolean) => {
    if (!isOpen) {
      pendingPlay.current = null;
    }
    setIsModalOpen(isOpen);
  };

  const handleFormSubmit = () => {
    const start = pendingPlay.current;
    pendingPlay.current = null;
    handleUnlockContent();
    setIsModalOpen(false);
    start?.();
  };

  return (
    <VideoGateContext.Provider value={{ requestPlayback }}>
      {children}
      <ModalPane isModalOpen={isModalOpen} setIsModalOpen={closeModal}>
        <div className='paywall-form'>
          {form ? (
            <CmsComponentSwitch
              components={[form]}
              handleFormSubmit={handleFormSubmit}
              formType={FormTypes.CMS}
              trackingId='GATED_VIDEO'
              usePageTheme
            />
          ) : null}
        </div>
      </ModalPane>
    </VideoGateContext.Provider>
  );
};

const VideoGate = ({
  gate,
  children,
}: {
  gate?: GateEntry | null;
  children: ReactNode;
}) => {
  const secretKey = secretKeyOf(gate);
  if (!secretKey || !gate) {
    return children;
  }

  return (
    <PaywallProvider
      cookieName={secretKey}
      unlockKey={secretKey}
      hasInvertedPaywallLogic={gate.fields?.invertLogic === true}
    >
      <VideoGateInner gate={gate}>{children}</VideoGateInner>
    </PaywallProvider>
  );
};

export default VideoGate;
